import React, { ReactElement } from 'react';
import moment from 'moment';
import { Filter, Button, Table, TableContainer } from '@odin-labs/components';
import { useGetWorkerJobsiteAccessSessionsQuery } from 'apollo/generated/client-operations';
import { AuthContext } from 'auth';
import { ArrowToBottomIcon } from 'components/icons';
import { LoadingError } from 'components/loadingError';
import { LockedFeatureAlert } from 'components/lockedFeatureAlert';
import { LockedWorkerAlert } from 'containers/worker/tabs/LockedWorkerAlert';
import { WorkerTabProps } from 'containers/worker/types';
import { getLookerReportUrl } from 'looker';
import { LookerFilter, LookerReport, LookerTimeRangeType } from 'looker/types';
import { getCurrentISOFormattedDate, momentISODateFormatter } from 'utils';
import { paginationSizePerPage as limit } from 'utils/constants';
import { getISOFormattedDate } from 'utils/dates';
import { resolveFilterDate } from 'utils/dateRangeFilters';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { NewHeader } from 'components/header/NewHeader';
import {
  getColumns,
  getFilterItems,
  getSessionStatusInput,
} from './WorkerTimecards.tables';
import { WorkerJobsiteAccessSessionsFilters } from './types';

export function WorkerTimecards(props: WorkerTabProps): ReactElement {
  const {
    worker,
    jobsiteNames,
    isLocked,
    refetchWorkerData,
    onTabApiChange,
    dateRange: persistedDateRange,
    setDateRange,
  } = props;

  // Seed defaults from the range persisted on the container (shared across
  // tabs), falling back to this tab's own defaults when nothing is persisted.
  const defaultStartDate = resolveFilterDate(
    persistedDateRange?.startDate,
    moment(getISOFormattedDate(moment().subtract(30, 'days'))),
  );
  const defaultEndDate = resolveFilterDate(
    persistedDateRange?.endDate,
    moment(getCurrentISOFormattedDate()),
  );
  const { currentUser: user } = React.useContext(AuthContext);

  const isFeatureLocked = !user.hasPaidAccess;

  const {
    page,
    jobsiteIds,
    contractorIds,
    sessionStatus,
    startDate,
    endDate,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams({
    defaultValues: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
  });

  const { data, loading, error } = useGetWorkerJobsiteAccessSessionsQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip:
      isUrlLoading ||
      !startDate ||
      !endDate ||
      !worker ||
      worker?.isArchived ||
      isLocked ||
      isFeatureLocked,
    variables: {
      workerId: worker.workerId,
      jobsiteAccessSessionsInput: {
        jobsiteIds,
        contractorIds,
        startDate: startDate?.toDate(),
        endDate: endDate?.toDate(),
        ...getSessionStatusInput(sessionStatus),
        paginationInput: {
          limit,
          offset: limit * page,
        },
      },
    },
  });

  const workerJobsiteAccessSessionsData = data?.getWorker.jobsiteAccessSessions;
  const workerJobsiteAccessSessions =
    workerJobsiteAccessSessionsData?.edges.map(({ node }) => node);
  const { filtersOptions } = workerJobsiteAccessSessionsData ?? {};
  const workerJobsiteAccessSessionsCount =
    workerJobsiteAccessSessionsData?.count ?? 0;
  const pageCount = Math.ceil(workerJobsiteAccessSessionsCount / limit);

  const columns = React.useMemo(() => getColumns(), []);
  const filterItems = React.useMemo(
    () =>
      getFilterItems({
        filtersOptions,
        jobsiteIds,
        contractorIds,
        sessionStatus,
        startDate,
        endDate,
      }),
    [
      filtersOptions,
      jobsiteIds,
      contractorIds,
      sessionStatus,
      startDate,
      endDate,
    ],
  );

  const lookerReportUrl = getLookerReportUrl(
    LookerReport.JobsiteAccessHistoryCompliance,
    {
      [LookerFilter.Jobsite]: jobsiteNames.join(),
      [LookerFilter.TimeRangeType]: LookerTimeRangeType.DateRange,
      [LookerFilter.StartDate]: startDate?.format(momentISODateFormatter),
      [LookerFilter.EndDate]: endDate?.format(momentISODateFormatter),
      [LookerFilter.WorkerQuickCode]: worker.quickCode,
    },
  );

  const onPageChangeHandler = (_pageSize: number, pageIndex: number): void => {
    updateUrl({ page: pageIndex ? pageIndex + 1 : null });
  };

  const onFilterChangeHandler = (
    changedFilters: Partial<WorkerJobsiteAccessSessionsFilters>,
  ): void => {
    const { dateRange: dateRangeFilter, ...restChangedFilters } =
      changedFilters;
    const dateRange =
      dateRangeFilter === null
        ? { startDate: null, endDate: null }
        : dateRangeFilter;
    updateUrl({ page: null, ...dateRange, ...restChangedFilters });
  };

  // Keep the shared container range in sync with the resolved date filter so
  // sibling tabs inherit it while this worker stays mounted. Keyed on the
  // formatted values so it fires only on real changes. Only the date keys are
  // written, so any time persisted by another tab is preserved on merge.
  const syncedStartDate = startDate?.format(momentISODateFormatter);
  const syncedEndDate = endDate?.format(momentISODateFormatter);
  React.useEffect(() => {
    if (isUrlLoading) return;
    setDateRange?.({ startDate: syncedStartDate, endDate: syncedEndDate });
  }, [setDateRange, syncedStartDate, syncedEndDate, isUrlLoading]);

  React.useEffect(
    () => onTabApiChange({ refetchData: refetchWorkerData }),
    [refetchWorkerData],
  );

  if (error) {
    return <LoadingError error={error} />;
  }

  if (isLocked) {
    return <LockedWorkerAlert worker={worker} />;
  }

  if (isFeatureLocked) {
    return <LockedFeatureAlert />;
  }

  return (
    <TableContainer>
      <NewHeader
        size="md"
        title="Timecards"
        titleInfo={workerJobsiteAccessSessionsCount}
        actionsProps={{ headerActions: null }}
      />
      <Filter
        items={filterItems}
        loading={loading}
        onChange={onFilterChangeHandler}
      >
        {!user.isContractor && (
          <Button
            as="a"
            href={lookerReportUrl}
            target="_blank"
            theme="white"
            size={['base', 'md:xs']}
            text="Download"
            hideTextOnMobile
            icon={ArrowToBottomIcon}
          />
        )}
      </Filter>
      <Table
        loading={loading}
        columns={columns}
        data={workerJobsiteAccessSessions}
        initialState={{ pageSize: limit }}
        pageCount={pageCount}
        pageIndex={page}
        remote
        enablePagination
        onPageChange={onPageChangeHandler}
        cellClassName="!odin-pl-5"
        disableGlobalFilter
        disableSortBy
      />
    </TableContainer>
  );
}
